import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, Firestore, onSnapshot } from 'firebase/firestore';
import { useFirestore } from '../helpers/firebaseContext';
import { InvestmentPerformance, InvestmentPerformanceTarget, isPerformancePathPart, kuwaitYear, parseInvestmentPerformance, performanceDocumentPath, performanceInvestmentKey, PerformanceSubject, totalPerformanceValues } from '../helpers/investmentPerformance';

export interface InvestmentPerformanceState {
  loading: boolean;
  summary?: InvestmentPerformance;
  error: string | null;
}

type PerformanceRecords = Record<string, InvestmentPerformanceState>;
interface PerformanceContextState {
  enabled: boolean;
  year: number;
  records: PerformanceRecords;
}
const PerformanceContext = createContext<PerformanceContextState>({ enabled: false, year: kuwaitYear(), records: {} });
const missing: InvestmentPerformanceState = { loading: false, error: null };

export const useInvestmentPerformance = (subject: PerformanceSubject, investmentId?: string) => {
  const { enabled, year, records } = useContext(PerformanceContext);
  const state = enabled && investmentId ? records[performanceInvestmentKey(subject, investmentId)] ?? missing : missing;
  return { enabled, year, ...state };
};

// Combined daily value of several investments already subscribed by the provider.
export const useInvestmentPerformanceTotal = (investments: InvestmentPerformanceTarget[]) => {
  const { enabled, year, records } = useContext(PerformanceContext);
  const states = enabled ? investments.map(target => records[performanceInvestmentKey(target.subject, target.investmentId)] ?? missing) : [];
  const summaries = states.flatMap(state => state.summary ? [state.summary] : []);
  return { enabled, year, loading: states.some(state => state.loading), points: totalPerformanceValues(summaries), asOfDate: summaries.map(summary => summary.asOfDate).sort().pop() };
};

// One subscription per investment supplies both headline figures and the chart.
// The existing holdings supply document IDs, independently of display codes.
export function subscribeInvestmentPerformance(db: Firestore, customerId: string, year: number, investments: InvestmentPerformanceTarget[], update: (key: string, state: InvestmentPerformanceState) => void) {
  let current = true;
  const seen = new Set<string>();
  const unsubscribe: (() => void)[] = [];
  for (const investment of investments) {
    const key = performanceInvestmentKey(investment.subject, investment.investmentId);
    if (seen.has(key)) continue;
    seen.add(key);
    try {
      const path = performanceDocumentPath(customerId, investment);
      unsubscribe.push(onSnapshot(doc(db, path[0], ...path.slice(1)), snapshot => {
        if (!current) return;
        try {
          const raw = snapshot.data();
          // The single current document is replaced each year. Until then, an
          // earlier year's publication is absent from the new year's YTD view.
          if (!snapshot.exists() || (Number.isInteger(raw?.year) && raw!.year < year)) {
            update(key, missing);
            return;
          }
          const summary = parseInvestmentPerformance(snapshot.id, raw!, year);
          if (summary.customerId !== customerId || summary.subjectType !== investment.subject || summary.productId !== investment.investmentId) {
            throw new Error('The published performance does not match this investment.');
          }
          update(key, { summary, loading: false, error: null });
        } catch (error) {
          update(key, { loading: false, error: error instanceof Error ? error.message : 'Performance data could not be read.' });
        }
      }, () => {
        if (current) update(key, { loading: false, error: 'Performance data could not be loaded. Refresh the page or contact support.' });
      }));
    } catch {
      update(key, { loading: false, error: 'The investment performance document could not be loaded.' });
    }
  }
  return () => { current = false; unsubscribe.forEach(stop => stop()); };
}

export function InvestmentPerformanceProvider({ customerId, enabled, investments, children }: { customerId?: string; enabled: boolean; investments: InvestmentPerformanceTarget[]; children: React.ReactNode }) {
  const db = useFirestore();
  const [year, setYear] = useState(() => kuwaitYear());
  const [state, setState] = useState<{ key: string; records: PerformanceRecords }>({ key: '', records: {} });
  // Stable serialized targets avoid reconnecting when the parent renders again.
  const targets = JSON.stringify([...new Map(investments.filter(target => isPerformancePathPart(target.investmentId)).map(target => [performanceInvestmentKey(target.subject, target.investmentId), target])).values()].sort((a, b) => performanceInvestmentKey(a.subject, a.investmentId).localeCompare(performanceInvestmentKey(b.subject, b.investmentId))));
  const key = JSON.stringify([customerId, year, enabled, targets]);
  const loadingRecords = () => Object.fromEntries((JSON.parse(targets) as InvestmentPerformanceTarget[]).map(target => [performanceInvestmentKey(target.subject, target.investmentId), { loading: true, error: null }]));

  useEffect(() => {
    const timer = setInterval(() => setYear(kuwaitYear()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!enabled || !customerId) return;
    const initial = loadingRecords();
    setState({ key, records: initial });
    return subscribeInvestmentPerformance(db, customerId, year, JSON.parse(targets), (investmentKey, result) => {
      setState(previous => ({ key, records: { ...(previous.key === key ? previous.records : initial), [investmentKey]: result } }));
    });
  }, [db, customerId, year, enabled, key, targets]);

  // Hide a previous customer's data immediately, before effect cleanup runs.
  const records = enabled && customerId ? state.key === key ? state.records : loadingRecords() : {};
  return <PerformanceContext.Provider value={{ enabled, year, records }}>{children}</PerformanceContext.Provider>;
}
